import React, { useCallback, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  updateProfile,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  unlink
} from 'firebase/auth';
import { ArrowRight, Eye, EyeOff, Mail, User } from 'lucide-react';

import { getHomePageUrl } from '../../utils/urlHelpers';
import { useAuth, useParams } from '../../hooks';
import { Button } from '../shared';
import { PRIVACY_URL, TERMS_OF_USE_URL, themeColorHex, USER_ROLES } from '../../constants';
import { Link, useNavigate } from 'react-router-dom';
import { createStudent } from '../../api';
import { removeUrlParam } from '../../utils';
import { useRefreshSubscription } from '../../queries';
import { LinkIcon, LockIcon } from '@chakra-ui/icons';
import { cn } from '../ui/utils';

const GOOGLE_PROVIDER_ID = GoogleAuthProvider.PROVIDER_ID;
const MICROSOFT_PROVIDER_ID = 'microsoft.com';
const PASSWORD_PROVIDER_ID = EmailAuthProvider.PROVIDER_ID;

const PROVIDER_LABELS = {
  [GOOGLE_PROVIDER_ID]: 'Google',
  [MICROSOFT_PROVIDER_ID]: 'Microsoft'
};

const LOGIN_BTN_PRIMARY = 'h-11 w-full font-semibold';
const LOGIN_BTN_SECONDARY =
  'h-11 w-full font-normal text-[#757575] shadow-sm hover:bg-muted/50 bg-white';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getProviderCustomParameters = (loginHint) => {
  return loginHint ? { login_hint: loginHint } : {};
};

const getSignInOptions = (eventOrOptions) => {
  if (eventOrOptions?.preventDefault) {
    eventOrOptions.preventDefault();
    return {};
  }

  return eventOrOptions || {};
};

function SocialPillButton({ icon, iconAlt, children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-white px-3 py-4 text-base font-semibold text-black shadow-[0_4px_8px_rgba(0,0,0,0.15)] transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      <img src={icon} alt={iconAlt} className="h-6 w-6 object-contain" width={24} height={24} />
      {children}
    </button>
  );
}

function PillField({ children }) {
  return (
    <div className="flex items-center rounded-full bg-white py-3 pl-5 pr-4 shadow-[0_4px_8px_rgba(0,0,0,0.15)]">
      {children}
    </div>
  );
}

function BackIconButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full text-white hover:bg-white/10"
      aria-label="Back"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
          fill="#FFFFFF"
        />
      </svg>
    </button>
  );
}

const ClasswiseLogin = () => {
  const { auth, user, signOut } = useAuth();
  const navigate = useNavigate();
  const { redirectTo } = useParams();
  const [authStep, setAuthStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isEmailSigningIn, setIsEmailSigningIn] = useState(false);
  const [isSocialSigningIn, setIsSocialSigningIn] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingMicrosoftCredential, setPendingMicrosoftCredential] = useState(null);
  const [providerSwitchRequest, setProviderSwitchRequest] = useState(null);
  const [isProviderSwitchLoading, setIsProviderSwitchLoading] = useState(false);
  const { mutate: refreshSubscription } = useRefreshSubscription();

  const emailIsValid = isValidEmail(email);
  const passwordIsValid = password.length >= 6;
  const busy = isCheckingEmail || isEmailSigningIn || isSocialSigningIn || isResettingPassword;

  const loginCallback = useCallback(
    async (result) => {
      if (result?.user) {
        const { email: userEmail, displayName, uid, accessToken } = result.user;
        const photoURL = result.user?.photoURL || '/assets/images/default-profile.png';

        await createStudent({
          token: accessToken,
          student: {
            email: userEmail,
            name: displayName,
            uid,
            role: USER_ROLES.STUDENT,
            displayPicture: photoURL
          }
        });

        refreshSubscription(result.user.accessToken, {
          onSuccess: (data) => {
            const { isChanged } = data;
            if (isChanged) {
              result?.user.getIdToken(true);
            }
          }
        });
      }
    },
    [refreshSubscription]
  );

  const checkParamsAndRedirect = React.useCallback(() => {
    const url = redirectTo ? redirectTo : getHomePageUrl();
    const cleanUrl = removeUrlParam(url, 'isLoggedIn');
    const [, queryString] = cleanUrl.split('?');
    const navigateUrl = queryString ? `${cleanUrl}&isLoggedIn=true` : `${cleanUrl}?isLoggedIn=true`;
    navigate(navigateUrl);
  }, [navigate, redirectTo]);

  const signInWithGoogle = useCallback(
    async (loginHint) => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters(getProviderCustomParameters(loginHint));
      return signInWithPopup(auth, provider);
    },
    [auth]
  );

  const signInWithMicrosoft = useCallback(
    async (loginHint) => {
      const provider = new OAuthProvider(MICROSOFT_PROVIDER_ID);
      provider.setCustomParameters(getProviderCustomParameters(loginHint));
      return signInWithPopup(auth, provider);
    },
    [auth]
  );

  const linkCredentialAndRemoveProvider = useCallback(async (authUser, pendingCredential, providerToRemove) => {
    const linkedResult = await linkWithCredential(authUser, pendingCredential);

    if (linkedResult?.user?.providerData?.some((provider) => provider.providerId === providerToRemove)) {
      const updatedUser = await unlink(linkedResult.user, providerToRemove);
      await updatedUser.reload?.();
    }

    return linkedResult;
  }, []);

  const handleProviderCollision = useCallback(
    async (error, targetProviderId) => {
      const pendingCredential =
        targetProviderId === MICROSOFT_PROVIDER_ID
          ? OAuthProvider.credentialFromError(error)
          : GoogleAuthProvider.credentialFromError(error);
      const collisionEmail = error?.customData?.email;

      if (
        error?.code !== 'auth/account-exists-with-different-credential' ||
        !pendingCredential ||
        !collisionEmail
      ) {
        return false;
      }

      const providers = await fetchSignInMethodsForEmail(auth, collisionEmail);

      if (targetProviderId === MICROSOFT_PROVIDER_ID && providers.includes(PASSWORD_PROVIDER_ID)) {
        setEmail(collisionEmail);
        setPendingMicrosoftCredential(pendingCredential);
        setIsNewUser(false);
        setAuthStep('password');
        setErrorMessage(
          'This email already uses password sign-in. Enter your password once to switch this account to Microsoft sign-in.'
        );
        return true;
      }

      if (targetProviderId === MICROSOFT_PROVIDER_ID && providers.includes(GOOGLE_PROVIDER_ID)) {
        setProviderSwitchRequest({
          email: collisionEmail,
          existingProviderId: GOOGLE_PROVIDER_ID,
          targetProviderId,
          pendingCredential
        });
        return true;
      }

      return false;
    },
    [auth]
  );

  const handleGoogleSignIn = async (eventOrOptions) => {
    const signInOptions = getSignInOptions(eventOrOptions);

    try {
      setIsSocialSigningIn(true);
      const result = await signInWithGoogle(signInOptions.loginHint);
      if (result?.user) {
        await loginCallback(result);
        checkParamsAndRedirect();
      }
    } catch (error) {
      if (await handleProviderCollision(error, GOOGLE_PROVIDER_ID)) return;
      console.error('Google sign-in failed:', error);
    } finally {
      setIsSocialSigningIn(false);
    }
  };

  const handleMicrosoftSignIn = async (eventOrOptions) => {
    const signInOptions = getSignInOptions(eventOrOptions);

    try {
      setIsSocialSigningIn(true);
      const result = await signInWithMicrosoft(signInOptions.loginHint);
      if (result?.user) {
        await loginCallback(result);
        checkParamsAndRedirect();
      }
    } catch (error) {
      if (await handleProviderCollision(error, MICROSOFT_PROVIDER_ID)) return;
      console.error('Microsoft sign-in failed:', error);
    } finally {
      setIsSocialSigningIn(false);
    }
  };

  const handleContinueWithMicrosoft = async () => {
    if (!providerSwitchRequest) return;

    setIsProviderSwitchLoading(true);
    try {
      const existingProviderResult = await signInWithGoogle(providerSwitchRequest.email);
      const switchedProviderResult = await linkCredentialAndRemoveProvider(
        existingProviderResult.user,
        providerSwitchRequest.pendingCredential,
        providerSwitchRequest.existingProviderId
      );

      setProviderSwitchRequest(null);
      await loginCallback(switchedProviderResult);
      checkParamsAndRedirect();
    } catch (error) {
      console.error('Microsoft account linking failed:', error);
      setErrorMessage('Could not link your Microsoft account. Please try again.');
    } finally {
      setIsProviderSwitchLoading(false);
    }
  };

  const handleUseGoogleInstead = async () => {
    if (!providerSwitchRequest) return;

    setIsProviderSwitchLoading(true);
    try {
      const result = await signInWithGoogle(providerSwitchRequest.email);
      setProviderSwitchRequest(null);
      await loginCallback(result);
      checkParamsAndRedirect();
    } catch (error) {
      console.error('Google sign-in failed:', error);
      setErrorMessage('Google sign-in failed. Please try again.');
    } finally {
      setIsProviderSwitchLoading(false);
    }
  };

  const handleEmailContinue = async () => {
    if (!emailIsValid || busy) return;

    setErrorMessage('');
    setIsCheckingEmail(true);
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email.trim());
      setIsNewUser(!(signInMethods && signInMethods.length > 0));
      setAuthStep('password');
    } catch (error) {
      console.log('Error checking user:', error);
      setIsNewUser(true);
      setAuthStep('password');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailAuth = async () => {
    if (isEmailSigningIn) return;
    if (isNewUser && !fullName.trim()) {
      setErrorMessage('Please enter your full name.');
      return;
    }
    if (!passwordIsValid) {
      setErrorMessage('Password must be at least 6 characters long.');
      return;
    }

    setErrorMessage('');
    setIsEmailSigningIn(true);

    try {
      if (isNewUser) {
        const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (!result) {
          setErrorMessage('Something went wrong! Try again.');
          return;
        }
        result.user.displayName = fullName.trim();
        await updateProfile(result.user, { displayName: fullName.trim() });
        await loginCallback(result);
        checkParamsAndRedirect();
        return;
      }

      const result = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (!result?.user) {
        setErrorMessage('Something went wrong! Try again.');
        return;
      }

      const loginResult = pendingMicrosoftCredential
        ? await linkCredentialAndRemoveProvider(result.user, pendingMicrosoftCredential, PASSWORD_PROVIDER_ID)
        : result;

      if (pendingMicrosoftCredential) {
        setPendingMicrosoftCredential(null);
      }

      await loginCallback(loginResult);
      checkParamsAndRedirect();
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setErrorMessage('No account found with this email.');
        setAuthStep('email');
      } else if (error.code === 'auth/missing-password') {
        setErrorMessage('Password cannot be empty');
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        const providers = await fetchSignInMethodsForEmail(auth, email.trim());
        if (providers.includes('password')) {
          setErrorMessage('Incorrect password. Please try again.');
        } else if (providers.includes('google.com')) {
          setErrorMessage('Previously signed in with Google. Redirecting to Google Sign in...');
          setTimeout(() => {
            handleGoogleSignIn({ loginHint: email.trim() });
          }, 2000);
        } else if (providers.includes(MICROSOFT_PROVIDER_ID)) {
          setErrorMessage('Previously signed in with Microsoft. Redirecting to Microsoft Sign in...');
          setTimeout(() => {
            handleMicrosoftSignIn({ loginHint: email.trim() });
          }, 2000);
        } else {
          setErrorMessage('Incorrect password. Please try again.');
        }
      } else if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Invalid email address.');
        setAuthStep('email');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/user-disabled') {
        setErrorMessage('This account has been disabled.');
      } else {
        setErrorMessage('Something went wrong! Try again later.');
      }
      console.log(error.code + ':' + error.message);
    } finally {
      setIsEmailSigningIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!emailIsValid) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setErrorMessage('');
    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, email.trim(), {
        url: import.meta.env.NEXT_PUBLIC_STUDENT_APP_URL
      });
      setErrorMessage('Check your email for a link to reset your password.');
    } catch (error) {
      if (error.code === 'auth/invalid-email') {
        setErrorMessage('Invalid Email');
      } else if (error.code === 'auth/user-not-found') {
        setErrorMessage('No account found with this email.');
      } else {
        setErrorMessage('Could not send reset email.');
      }
      console.log('error:', error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleBackToEmail = () => {
    setAuthStep('email');
    setPassword('');
    setFullName('');
    setIsNewUser(true);
    setShowPassword(false);
    setErrorMessage('');
    setPendingMicrosoftCredential(null);
  };

  React.useEffect(() => {
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute('content');
    themeMeta?.setAttribute('content', '#002A4E');

    return () => {
      themeMeta?.setAttribute('content', previousTheme || '#ffffff');
    };
  }, []);

  React.useEffect(() => {
    window.__classwiseGoogleAuthHandler__ = async (response) => {
      const idToken = response.credential;
      const credential = GoogleAuthProvider.credential(idToken);
      try {
        const result = await signInWithCredential(auth, credential);
        await loginCallback(result);
        checkParamsAndRedirect();
      } catch (error) {
        console.error('firebase login with credential error: ', error);
      }
    };
    setTimeout(() => {}, 1000);
  }, [auth, checkParamsAndRedirect, loginCallback]);

  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevRootBg = root?.style.backgroundColor ?? '';

    html.style.backgroundColor = '#020817';
    body.style.backgroundColor = '#020817';
    if (root) root.style.backgroundColor = '#020817';

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (root) root.style.backgroundColor = prevRootBg;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-[#020817]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[url('/assets/images/login-bg.webp')] bg-cover bg-[center_68%] bg-no-repeat md:bg-[center_62%]"
      />
      <div className="pointer-events-none absolute inset-0 bg-[#020817]/20" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#020817]/70 to-transparent" />

      <Modal
        isOpen={!!providerSwitchRequest}
        onClose={() => setProviderSwitchRequest(null)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent mx={4} maxW="432px" borderRadius="10px" px={{ base: 6, sm: 8 }} py={8}>
          <Flex justify="center" mb={7}>
            <Flex
              align="center"
              justify="center"
              gap={4}
              bg="#eef4ff"
              borderRadius="999px"
              px={5}
              py={3}
            >
              <Flex
                align="center"
                justify="center"
                boxSize="56px"
                bg="#FFFFFF"
                borderRadius="full"
                boxShadow="0 10px 25px rgba(15, 23, 42, 0.08)"
              >
                <img src="/assets/images/google.png" alt="Google" className="h-8 w-8 object-contain" />
              </Flex>
              <LinkIcon color={themeColorHex} boxSize={5} />
              <Flex
                align="center"
                justify="center"
                boxSize="56px"
                bg="#FFFFFF"
                borderRadius="full"
                boxShadow="0 10px 25px rgba(15, 23, 42, 0.08)"
              >
                <img src="/assets/images/microsoft.png" alt="Microsoft" className="h-8 w-8 object-contain" />
              </Flex>
            </Flex>
          </Flex>

          <ModalHeader p={0} fontSize="24px" lineHeight="32px" fontWeight="700" color="#111827">
            Link your {PROVIDER_LABELS[providerSwitchRequest?.targetProviderId]} account
          </ModalHeader>

          <ModalBody p={0} mt={5}>
            <Text fontSize="16px" lineHeight="28px" color="#374151">
              Your Classwise account is currently connected to{' '}
              <Text as="span" fontWeight="700" color="#111827">
                {PROVIDER_LABELS[providerSwitchRequest?.existingProviderId]}
              </Text>
              .
            </Text>

            <Flex gap={4} mt={5} align="flex-start">
              <Flex
                align="center"
                justify="center"
                boxSize="46px"
                flexShrink={0}
                bg="#eaf2ff"
                color={themeColorHex}
                borderRadius="full"
              >
                <LockIcon boxSize={5} />
              </Flex>
              <Text fontSize="16px" lineHeight="28px" color="#4b5563">
                After you click Continue, we&apos;ll ask you to sign in with{' '}
                {PROVIDER_LABELS[providerSwitchRequest?.existingProviderId]} once to verify
                ownership of your account.
              </Text>
            </Flex>

            <Text fontSize="16px" lineHeight="28px" color="#4b5563" mt={5}>
              Then we&apos;ll connect your {PROVIDER_LABELS[providerSwitchRequest?.targetProviderId]}{' '}
              account so you can sign in with{' '}
              {PROVIDER_LABELS[providerSwitchRequest?.targetProviderId]}.
            </Text>
          </ModalBody>

          <ModalFooter p={0} mt={8} gap={3} flexDirection={{ base: 'column-reverse', sm: 'row' }}>
            <Button
              type="button"
              buttonType="secondary"
              className={`${LOGIN_BTN_SECONDARY} sm:flex-1 font-semibold text-primary`}
              onClick={handleUseGoogleInstead}
              disabled={isProviderSwitchLoading}
            >
              Use {PROVIDER_LABELS[providerSwitchRequest?.existingProviderId]} instead
            </Button>
            <Button
              type="button"
              className={`${LOGIN_BTN_PRIMARY} sm:flex-1`}
              onClick={handleContinueWithMicrosoft}
              disabled={isProviderSwitchLoading}
            >
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="relative z-10 flex h-full min-h-0 w-full flex-col items-center justify-start overflow-y-auto overscroll-none px-6 pb-[max(2.5rem,env(safe-area-inset-bottom,0px))] pt-[max(5rem,calc(12dvh+env(safe-area-inset-top,0px)))] md:pt-[16dvh]">
        <div className="mx-auto w-full max-w-[400px]">
          {authStep === 'email' && (
            <div className="flex flex-col items-center">
              <img
                src="/assets/images/logo.png"
                alt="Classwise"
                className="mb-3 h-14 w-auto brightness-0 invert sm:mb-4 md:mb-5 md:h-14"
              />
              <h1 className="mb-4 text-center text-[clamp(1.125rem,3.5vw,1.5rem)] font-bold text-white sm:mb-5">
                AI Powered Learning
              </h1>

              <div className="mb-4 flex w-[90%] gap-3">
                <SocialPillButton
                  icon="/assets/images/google.png"
                  iconAlt="Google"
                  onClick={handleGoogleSignIn}
                  disabled={busy}
                >
                  {isSocialSigningIn ? 'Signing in...' : 'Google'}
                </SocialPillButton>
                <SocialPillButton
                  icon="/assets/images/microsoft.png"
                  iconAlt="Microsoft"
                  onClick={handleMicrosoftSignIn}
                  disabled={busy}
                >
                  Microsoft
                </SocialPillButton>
              </div>

              <form
                className="mb-4 flex w-[90%] items-center rounded-full bg-white py-1.5 pl-5 pr-1.5 shadow-[0_4px_8px_rgba(0,0,0,0.15)]"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleEmailContinue();
                }}
              >
                <Mail className="mr-3 h-6 w-6 shrink-0 text-[#666666]" aria-hidden />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Enter your email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={busy}
                  className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base text-black outline-none placeholder:text-[#999999]"
                />
                <button
                  type="submit"
                  disabled={busy || !emailIsValid}
                  className={cn(
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
                    emailIsValid ? 'bg-[#4A90E2]' : 'bg-[#D1D5DB]'
                  )}
                  aria-label="Continue with email"
                >
                  <ArrowRight className="h-5 w-5 text-white" />
                </button>
              </form>

              {errorMessage && (
                <p className="mb-3 w-[90%] text-center text-sm text-red-200" role="alert">
                  {errorMessage}
                </p>
              )}

              <Box id="classwise-google-login">
                <Box
                  id="g_id_onload"
                  position="relative"
                  data-client_id={import.meta.env.NEXT_PUBLIC_CLIENT_ID}
                  data-callback="__classwiseGoogleAuthHandler__"
                  data-prompt_parent_id="classwise-google-login"
                  data-cancel_on_tap_outside="true"
                />
              </Box>

              <p className="mt-2 w-[90%] text-center text-xs leading-relaxed text-white/70">
                By signing up for a Classwise account you are agreeing to our{' '}
                <Link
                  to={TERMS_OF_USE_URL}
                  target="_blank"
                  className="underline underline-offset-2 hover:text-white"
                >
                  terms of service
                </Link>{' '}
                and{' '}
                <Link
                  to={PRIVACY_URL}
                  target="_blank"
                  className="underline underline-offset-2 hover:text-white"
                >
                  data privacy policy
                </Link>
                .
              </p>

              {user && (
                <button
                  type="button"
                  onClick={signOut}
                  className="mt-4 text-sm font-medium text-white underline"
                >
                  Sign out
                </button>
              )}
            </div>
          )}

          {authStep === 'forgot-password' && (
            <div className="mt-2 w-full px-1">
              <div className="relative mb-5 text-center">
                <BackIconButton onClick={() => setAuthStep('password')} />
                <h2 className="text-[28px] font-bold text-white">Reset Password</h2>
                <p className="mt-2 text-[15px] text-[#E5E7EB]">Enter email to receive instructions</p>
              </div>

              <div className="mb-4">
                <PillField>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Email Address"
                    autoComplete="email"
                    disabled={isResettingPassword}
                    className="min-w-0 flex-1 border-0 bg-transparent text-base text-black outline-none placeholder:text-[#9CA3AF]"
                  />
                  <Mail className="ml-2 h-6 w-6 shrink-0 text-[#9CA3AF]" aria-hidden />
                </PillField>
              </div>

              {errorMessage && (
                <p className="mb-3 text-center text-sm text-red-200" role="alert">
                  {errorMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={!emailIsValid || isResettingPassword}
                className={cn(
                  'mt-5 w-full rounded-full py-4 text-base font-semibold text-white shadow-[0_4px_8px_rgba(99,102,241,0.3)]',
                  !emailIsValid || isResettingPassword ? 'bg-[#9CA3AF] shadow-none' : 'bg-[#6366F1]'
                )}
              >
                {isResettingPassword ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          )}

          {authStep === 'password' && (
            <div className="w-full px-1">
              <div className="relative mb-5 text-center">
                <BackIconButton onClick={handleBackToEmail} />
                <h2 className="text-[28px] font-bold text-white">
                  {isNewUser ? 'Create Account' : 'Welcome Back'}
                </h2>
                <p className="mt-2 text-[15px] text-[#E5E7EB]">
                  {isNewUser ? `Sign up with ${email}` : `Sign in to ${email}`}
                </p>
              </div>

              {isNewUser && (
                <div className="mb-4">
                  <PillField>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      placeholder="Full Name"
                      autoComplete="name"
                      autoCapitalize="words"
                      disabled={busy}
                      className="min-w-0 flex-1 border-0 bg-transparent text-base text-black outline-none placeholder:text-[#9CA3AF]"
                    />
                    <User className="ml-2 h-6 w-6 shrink-0 text-[#9CA3AF]" aria-hidden />
                  </PillField>
                </div>
              )}

              <div className="mb-4">
                <PillField>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Password"
                    autoComplete={isNewUser ? 'new-password' : 'current-password'}
                    disabled={busy}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleEmailAuth();
                    }}
                    className="min-w-0 flex-1 border-0 bg-transparent text-base text-black outline-none placeholder:text-[#9CA3AF]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="ml-2 text-[#9CA3AF]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <Eye className="h-6 w-6" /> : <EyeOff className="h-6 w-6" />}
                  </button>
                </PillField>

                {!isNewUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setErrorMessage('');
                      setAuthStep('forgot-password');
                    }}
                    className="mt-3 block w-full text-right text-[15px] font-medium text-[#E5E7EB]"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>

              {errorMessage && (
                <p className="mb-3 text-center text-sm text-red-200" role="alert">
                  {errorMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleEmailAuth}
                disabled={
                  busy || !passwordIsValid || (isNewUser && !fullName.trim())
                }
                className={cn(
                  'w-full rounded-full py-4 text-base font-semibold text-white shadow-[0_4px_8px_rgba(99,102,241,0.3)]',
                  busy || !passwordIsValid || (isNewUser && !fullName.trim())
                    ? 'bg-[#9CA3AF] shadow-none'
                    : 'bg-[#6366F1]'
                )}
              >
                {isEmailSigningIn
                  ? isNewUser
                    ? 'Signing Up...'
                    : 'Signing In...'
                  : isNewUser
                    ? 'Sign Up'
                    : 'Sign In'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClasswiseLogin;
